import { handleEncryptionAccessDisabled } from "~vue/api/encryptionAccess";
import { logError } from "~vue/utils/logUtils";
import { generateRequestId, REQUEST_ID_HEADER } from "~vue/utils/requestId";
import type { Emitter } from "mitt";

import { getCookie } from "../../utils";

interface StreamingError extends Error {
    status: number;
    data: Record<string, unknown>;
}

function parseSseFrame(frame: string): Record<string, unknown> | null {
    if (!frame.trim() || frame.startsWith(":")) {
        return null;
    }

    const dataLine = frame.split("\n").find((line) => line.startsWith("data: "));
    if (!dataLine) {
        return null;
    }

    let parsed;
    try {
        parsed = JSON.parse(dataLine.slice("data: ".length));
    } catch {
        return null;
    }

    if (parsed.type === "stream_complete") {
        return null;
    }

    return parsed;
}

async function buildStreamingError(response: Response): Promise<StreamingError> {
    let errorData: Record<string, unknown> = {};
    try {
        errorData = await response.json();
    } catch {
        errorData = { error: response.statusText };
    }
    const errorMessage = typeof errorData.error === "string" ? errorData.error : `HTTP ${response.status}: ${response.statusText}`;
    const err = new Error(errorMessage) as StreamingError;
    err.status = response.status;
    err.data = errorData;
    return err;
}

export function useSendStreamingEvent(
    streamingPostUrl: string,
    emitter: Emitter<Record<string, unknown>>,
    eventContext: Record<string, unknown> = {},
): (eventType: string, data: Record<string, unknown>) => Promise<boolean> {
    return async function sendStreamingEvent(eventType: string, data: Record<string, unknown>): Promise<boolean> {
        const csrfToken = getCookie("csrftoken");
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            [REQUEST_ID_HEADER]: generateRequestId(),
        };
        if (csrfToken) {
            headers["X-CSRFToken"] = csrfToken;
        }

        const body = JSON.stringify({ event_type: eventType, ...eventContext, ...data });

        // Native fetch is required for ReadableStream access (SSE streaming).
        const response = await fetch(streamingPostUrl, {
            method: "POST",
            headers,
            body,
            credentials: "same-origin",
        });

        if (!response.ok) {
            handleEncryptionAccessDisabled(response);
            throw await buildStreamingError(response);
        }

        const reader = response.body?.getReader();
        if (!reader) {
            return true;
        }

        const decoder = new TextDecoder();
        let buffer = "";
        let handlerFailed = false;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const frames = buffer.split("\n\n");
                buffer = frames.pop() ?? "";

                for (const frame of frames) {
                    const parsed = parseSseFrame(frame);
                    if (parsed) {
                        handlerFailed ||= parsed.type === "chat_error";
                        emitter.emit(parsed.type as string, parsed);
                    }
                }
            }

            return !handlerFailed;
        } catch (error) {
            logError(error, {
                context: { component: "useSendStreamingEvent", eventType, url: streamingPostUrl },
                tags: { error_type: "sse_streaming_event" },
            });
            throw error;
        } finally {
            reader.releaseLock();
        }
    };
}
